/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import EffectSequence from '../Effects/EffectSequence';
import Currency, { CurrencyType } from '../Localization/Currency';
import ObservableCurrencyComponent from '../Localization/ObservableCurrencyComponent';
import Bangup from '../Playbar/Bangup';
import BetManager from '../Playbar/BetManager';
import Communicator from '../Server/Communicator';
import { IBonusGamePlayResult } from '../Server/Object/BaseGamePlayResult';
import IPlayResultComponent from '../Server/Object/IPlayResultComponent';
import PlayResultComponent from '../Server/PlayResultComponent';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateManager from '../StateManager/StateManager';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import ObservableBooleanComponent from '../Utility/Component/Observable/ObservableBooleanComponent';
import ObservableDoubleComponent from '../Utility/Component/Observable/ObservableDoubleComponent';
import ObservableIntComponent from '../Utility/Component/Observable/ObservableIntComponent';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import { ClientEvent } from './Integrations/ClientEvent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Managing class for bonus game logic and progression. */
@cc._decorator.ccclass
export default class BonusGame extends StateDependencyComponent
{
    /** The state in the base game StateManager on which to transition to a bonus game. */
    @property({ type: StateReference, visible: true })
    private _startState: StateReference = new StateReference(StateID.Bonus, true);

    /** Flags for the bonus game. */
    @property({ type: [cc.Float], visible: true })
    private _bonusGameFlags: number[] = [];

    /** Whether bonus is currently running. */
    @property({ type: ObservableBooleanComponent, visible: true })
    protected isActiveObservable: ObservableBooleanComponent = null;

    @property
    protected showSpins: boolean = true;

    /** Whether bonus is currently running. */
    @property({ type: ObservableBooleanComponent, visible: true })
    protected showSpinsObservable: ObservableBooleanComponent = null;

    /** Trigger prize observable. */
    @property({ type: ObservableCurrencyComponent, visible: true })
    private _triggerAwardObservable: ObservableCurrencyComponent = null;

    /** Retrigger spins. */
    @property({ type: ObservableIntComponent, visible: true })
    protected retriggerSpinsObservable: ObservableIntComponent = null;

    /** Inital bonus spin count observable. */
    @property({ type: ObservableDoubleComponent, visible: true })
    private _initialSpinCountObservable: ObservableDoubleComponent = null;

    /** Current spin index. */
    @property({ type: ObservableIntComponent, visible: true })
    private _currentSpin: ObservableIntComponent = null;

    /** Current number of total spin, retrigger can increase. */
    @property({ type: ObservableIntComponent, visible: true })
    private _currentTotalSpins: ObservableIntComponent = null;

    @property()
    protected maxRespins: number = 90;

    private _totalSpins: number = 6;

    /** @hidden */
    @property({ type: InjectableComponent, visible: true })
    private _bonusStateManagerRef: InjectableComponent<StateManager>
        = new InjectableComponent(ReferenceGroup.BonusGame_0);

    /** @hidden */
    @property({ type: InjectableComponent, visible: true })
    private _playResultRef: InjectableComponent<PlayResultComponent>
        = new InjectableComponent(ReferenceGroup.MainGame);

    /** @hidden */
    @property({ type: InjectableComponent, visible: true })
    private _betManagerRef: InjectableComponent<BetManager>
        = new InjectableComponent<BetManager>(ReferenceGroup.MainGame);

    /** @hidden */
    @property({ type: InjectableComponent, visible: true })
    private _mainGameBangupRef: InjectableComponent<Bangup>
        = new InjectableComponent(ReferenceGroup.MainGame);

    /** @hidden */
    @property({ type: InjectableComponent, visible: true })
    private _bonusBangupRef: InjectableComponent<Bangup>
        = new InjectableComponent(ReferenceGroup.BonusGame_0);

    /** @hidden */
    @property({ type: InjectableComponent, visible: true })
    protected retriggerEffects: InjectableComponent<EffectSequence>
        = new InjectableComponent(ReferenceGroup.BonusGame_0, "State.Custom.FreeSpin.Retrigger");

    /** @hidden */
    @property({ type: InjectableComponent, visible: true })
    protected maxRetriggerEffects: InjectableComponent<EffectSequence>
        = new InjectableComponent(ReferenceGroup.BonusGame_0, "State.Custom.FreeSpin.MaxRetrigger");

    private _communicatorRef: InjectableComponent<Communicator> = new InjectableComponent();

    /** If true, will subtract the winMC and add it back for main bangup. */
    @property
    protected excludeInitialTriggerAward: boolean = true;

    @property
    protected resetValueOnStart: boolean = true;

    protected setContexts(): void
    {
        super.setContexts();
        this._bonusStateManagerRef.SetContext(StateManager);
        this._playResultRef.SetContext(PlayResultComponent);
        this._betManagerRef.SetContext(BetManager);
        this._mainGameBangupRef.SetContext(Bangup);
        this._bonusBangupRef.SetContext(Bangup);

        this.retriggerEffects.SetContext(EffectSequence);
        this.maxRetriggerEffects.SetContext(EffectSequence);

        this._communicatorRef.SetContext(Communicator);
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._startState);

        this._playResultRef.WithValue(playResult => playResult.OnResult(() => this.updateBonusSpinValues()));

        this.retriggerEffectHandler = this.retriggerEffectHandler.bind(this);
        this.retriggerEffects.WithValue(this.retriggerEffectHandler);
        this.maxRetriggerEffects.WithValue(this.retriggerEffectHandler);

        this._bonusStateManagerRef.WithValue(stateManager =>
            {
                stateManager.OnComplete(() => this.endBonus());
            });
    }

    /** Binds onComplete functionality to effect sequence. */
    private retriggerEffectHandler(effectSequence: EffectSequence): void
    {
        effectSequence.OnComplete(() => this.updateCurrentTotal());
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (event.State === this._startState.Value)
        {
            if (!this._playResultRef.Value.HasBonus())
            {
                this.completeState(this._startState.Value);
            }
            else if (this.isThisBonus(this._playResultRef.Value))
            {
                this.reportClientEvent(ClientEvent.BONUS_START);
                this.updateBonusSpinValues(this.resetValueOnStart);
                this._bonusStateManagerRef.Value.StartPlay();
            }
        }
    }

    /** @hidden */
    private updateBonusSpinValues = (resetTotal: boolean = false): void =>
    {
        const bonusGameResult: IBonusGamePlayResult
            = this._playResultRef.Value.ResultData.PlayResult.BonusGamePlayResult;

        if (!bonusGameResult) { return; }

        if (this.isActiveObservable.Value)
        {
            this.reportClientEvent(ClientEvent.BONUS_SPIN);
        }

        if (resetTotal)
        {
            this.isActiveObservable.Value = true;
            if (this.showSpinsObservable && this.showSpins) { this.showSpinsObservable.Value = true; }
        }

        const triggerAward: number = this.getTriggerAward(bonusGameResult);
        const initialSpinCount: number = this.getInitialSpinCount(bonusGameResult);

        const currentSpin: number = this.getCurrentSpin(bonusGameResult, initialSpinCount);
        const currentTotalSpins: number = this.getCurrentTotalSpin(bonusGameResult, initialSpinCount);

        if (resetTotal)
        {
            this.resetTotal(initialSpinCount, triggerAward);
        }
        else if (currentSpin === 0) { return; }

        if (this.retriggerEffects.HasValue())
        {
            this.activateRetrigger(currentTotalSpins);
        }
        else
        {
            this.updateCurrentTotal();
        }

        this.updateTriggerAward(triggerAward);

        if (this._initialSpinCountObservable) { this._initialSpinCountObservable.Value = initialSpinCount; }
        if (this._currentSpin) { this._currentSpin.Value = currentSpin; }
        this._totalSpins = currentTotalSpins;
    }

    /**
     * Checks the bonus flags on play result to determine if this is a bonus.
     * @param playResult The play result to check the flags on.
     * @returns If the play result's bonus flag is valid.
     */
    private isThisBonus(playResult: IPlayResultComponent): boolean
    {
        const bonusFlag: number = playResult.GetBonusFlag();
        const bonusFlagIndex: number = this._bonusGameFlags.indexOf(bonusFlag);
        return bonusFlagIndex !== -1;
    }

    /** @hidden */
    private endBonus(): void
    {
        this.updateBangupValues();

        this._currentTotalSpins.Value = 0;
        this.isActiveObservable.Value = false;

        if (this.showSpinsObservable) { this.showSpinsObservable.Value = false; }

        this.reportClientEvent(ClientEvent.BONUS_END);
        this.completeState(this._startState.Value);
    }

    private updateBangupValues(): void
    {
        if (!this.excludeInitialTriggerAward)
        {
            this.adjustPaylineValue();
        }

        this._mainGameBangupRef.WithValue(mainGameBangup =>
            {
                this._bonusBangupRef.WithValue(bonusGameBangup =>
                    {
                        if (mainGameBangup === bonusGameBangup) { return; }

                        if (this.excludeInitialTriggerAward)
                        {
                            this.addWinMcAmount(this._triggerAwardObservable.Currency.McValue.Value);
                        }

                        const bangupAmount: Currency
                            = new Currency(this._betManagerRef.Value.ActiveDenom,
                                this._playResultRef.Value.PlayState.winMc);

                        bonusGameBangup.Clear();
                        mainGameBangup.SetTarget(bangupAmount);
                    });
            });
    }

    private reportClientEvent(clientEvent: ClientEvent): void
    {
        this._communicatorRef.WithValue(communicator => communicator.ReportClientEvent(clientEvent),
            () => CLog.error('No communicator ref.', this, LogTypes.Communicator));
    }

     /** Helper to adjust current amount. */
     private addWinMcAmount(amount: number): void
     {
         this._playResultRef.WithValue(playResult =>
             {
                 if (!playResult.PlayState) { return; }

                 playResult.PlayState.winMc += amount;
             });
     }

     private adjustPaylineValue(add: boolean = true): void
     {
         this._playResultRef.WithValue(playResult =>
            {
                let totalPay: number
                    = playResult.GetBaseGameResult().paylinePayout.reduce((payout, total) => total += payout, 0);
                if (totalPay < 1) { return; }

                this._betManagerRef.WithValue(betManager =>
                    {
                        totalPay
                            = new Currency(
                                betManager.ActiveDenomObservable,
                                totalPay,
                                CurrencyType.CREDITS).GetValue();

                        playResult.PlayState.winMc += add ? totalPay : -totalPay;
                    });
            });
     }

    // #region Update Bonus Spin Helpers

    /** Helper to update TriggerAward and Bangup. */
    private updateTriggerAward(triggerAward: number): void
    {
        if (!this._triggerAwardObservable) { return; }

        this._triggerAwardObservable.SetValue(triggerAward, CurrencyType.CREDITS);
        if (this.excludeInitialTriggerAward)
        {
            this.addWinMcAmount(-this._triggerAwardObservable.Currency.McValue.Value);
        }
        else
        {
            this.adjustPaylineValue(false);
        }
    }

    /** Helper to update currentTotalSpins. */
    private updateCurrentTotal = (): void =>
    {
        if (this._currentTotalSpins) { this._currentTotalSpins.Value = this._totalSpins; }
    }

    /** Helper to activate retrigger or maxRetrigger. */
    private activateRetrigger(currentTotalSpins: number): void
    {
        const triggerMax: boolean = this.maxRetriggerEffects.Value !== null;
        const retriggerAmount: number = currentTotalSpins - this._currentTotalSpins.Value;

        if (retriggerAmount > 0)
        {
            if (this.retriggerSpinsObservable.HasValue())
            {
                this.retriggerSpinsObservable.Value = retriggerAmount;
            }

            const isUnderMax: boolean = currentTotalSpins < this.maxRespins;

            this.retriggerEffects.Value.node.active = isUnderMax;
            if (triggerMax) { this.maxRetriggerEffects.Value.node.active = !isUnderMax; }
            return;
        }

        this.retriggerEffects.Value.node.active = false;
        if (triggerMax) { this.maxRetriggerEffects.Value.node.active = false; }
    }

    /** Helper to reset total spins and bangup. */
    private resetTotal(initialSpinCount: number, triggerAward: number): void
    {
        this._totalSpins = this._currentTotalSpins.Value = initialSpinCount;
        if (this.excludeInitialTriggerAward || triggerAward < 1) { return; }
        this._bonusBangupRef.WithValue(bangup =>
            {
                bangup.SetCurrent(new Currency(this._betManagerRef.Value.ActiveDenomObservable,
                    triggerAward, CurrencyType.CREDITS));
            });
    }

    /** @hidden */
    private getTriggerAward(bonusGameResult: IBonusGamePlayResult): number
    {
        try
        {
            return bonusGameResult.InitialTriggerAwardData
                ? bonusGameResult.InitialTriggerAwardData.nInitialTriggerPrize
                : bonusGameResult.Game.nInitialGameTriggerPrize;
        } catch (error){ CLog.warn(error, LogTypes.Bonus, this); }
        return 0;
    }

    /** @hidden */
    private getInitialSpinCount(bonusGameResult: IBonusGamePlayResult): number
    {
        try
        {
            return bonusGameResult.SpinData
                ? bonusGameResult.SpinData.nInitialPlays
                : bonusGameResult.TotalFreeSpins;
        } catch (error) { CLog.warn(error, LogTypes.Bonus, this); }
        return 0;
    }

    /** @hidden */
    private getCurrentSpin(bonusGameResult: IBonusGamePlayResult, def: number): number
    {
        try
        {
            return bonusGameResult.SpinData
                ? bonusGameResult.SpinData.nPlayNumber
                : bonusGameResult.CurrentSpinNumber;
        } catch (error) { CLog.warn(error, LogTypes.Bonus, this); }
        return def;
    }

    /** @hidden */
    private getCurrentTotalSpin(bonusGameResult: IBonusGamePlayResult, def: number): number
    {
        try
        {
            return bonusGameResult.SpinData
                ? bonusGameResult.SpinData.nTotalPlays
                : bonusGameResult.TotalFreeSpins;
        } catch (error) { CLog.warn(error, LogTypes.Bonus, this); }
        return def;
    }

    // #endregion
}
